import type { optionType } from './dictionary.ts'
import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { UserId } from './ids.ts'
import type { ConsentsData } from './Org.ts'
import type { SurveyTypes } from './Survey.ts'
import type { UserName } from './User.ts'

export enum JobRequestStatus {
    APPLIED = 'applied',
    ACCEPTED = 'accepted',
    REJECTED = 'rejected'
}

export enum JobRequestStage {
    DECLINED = -2,
    SOFT_NO = -1,
    NEW = 0,
    SHORTLISTED = 1,
    INTERVIEW = 2,
    OFFER = 4,
    HIRED = 5
}

/**
 * Lightweight follow-up pipeline an admin drives by hand on /admin/job-outcomes.
 *
 * Deliberately separate from `JobRequestStage`, which is the organisation's own hiring
 * pipeline: this one tracks *our* collection of the success fee, and applies to every
 * outcome, not only the ones where someone was hired.
 *
 * There is intentionally no "confirmed" stage. Whether the organisation acknowledged the
 * hire is already derived from its own record (`orgConfirmation`) and updates itself when
 * the org marks someone hired, so storing it again by hand would only let the two drift.
 */
export enum JobOutcomeStage {
    NEW = 'new',
    CONTACTED = 'contacted',
    INVOICED = 'invoiced',
    PAID = 'paid',
    DISPUTED = 'disputed',
    CLOSED = 'closed'
}

export const jobOutcomeStageDict: { [key in JobOutcomeStage]: string } = {
    [JobOutcomeStage.NEW]: 'Nové',
    [JobOutcomeStage.CONTACTED]: 'Oslovené',
    [JobOutcomeStage.INVOICED]: 'Vyfakturované',
    [JobOutcomeStage.PAID]: 'Zaplatené',
    [JobOutcomeStage.DISPUTED]: 'Sporné',
    [JobOutcomeStage.CLOSED]: 'Uzavreté'
}

/** Order shown in the picker — roughly the order a case moves through. */
export const jobOutcomeStageOrder: JobOutcomeStage[] = [
    JobOutcomeStage.NEW,
    JobOutcomeStage.CONTACTED,
    JobOutcomeStage.INVOICED,
    JobOutcomeStage.PAID,
    JobOutcomeStage.DISPUTED,
    JobOutcomeStage.CLOSED
]

/** Nothing left to do — hidden from the worklist by default. */
export const jobOutcomeTerminalStages: JobOutcomeStage[] = [JobOutcomeStage.PAID, JobOutcomeStage.CLOSED]

export enum JobRequestContractType {
    TPP = 'tpp',
    PART_TIME = 'part_time',
    AGREEMENT = 'agreement',
    CONTRACT = 'contract'
}

// Short labels used on cards / chips
export const jobRequestContractTypeDict: { [key in JobRequestContractType]: string } = {
    [JobRequestContractType.TPP]: 'TPP',
    [JobRequestContractType.PART_TIME]: 'Čiastočný úväzok',
    [JobRequestContractType.AGREEMENT]: 'Dohoda',
    [JobRequestContractType.CONTRACT]: 'Kontrakt'
}

// Descriptive labels used in the picker
export const jobRequestContractTypeOptions: optionType[] = [
    {
        id: JobRequestContractType.TPP,
        value: JobRequestContractType.TPP,
        label: 'TPP (trvalý pracovný pomer)'
    },
    {
        id: JobRequestContractType.PART_TIME,
        value: JobRequestContractType.PART_TIME,
        label: 'Čiastočný úväzok (part-time)'
    },
    {
        id: JobRequestContractType.AGREEMENT,
        value: JobRequestContractType.AGREEMENT,
        label: 'Dohoda (dohoda o práci)'
    },
    {
        id: JobRequestContractType.CONTRACT,
        value: JobRequestContractType.CONTRACT,
        label: 'Kontrakt (živnosť / faktúra)'
    }
]

export const jobRequestStageDict: { [key in JobRequestStage]: string } = {
    [JobRequestStage.DECLINED]: 'Zamietnutý',
    [JobRequestStage.SOFT_NO]: 'Skôr nie',
    [JobRequestStage.NEW]: 'Nový',
    [JobRequestStage.SHORTLISTED]: 'Predvýber',
    [JobRequestStage.INTERVIEW]: 'Pohovor',
    [JobRequestStage.OFFER]: 'Ponuka',
    [JobRequestStage.HIRED]: 'Prijatý'
}

export const jobRequestStageOptions: optionType[] = Object.values(JobRequestStage)
    .filter((stage): stage is JobRequestStage => typeof stage === 'number')
    .sort((a, b) => a - b)
    .map((stage) => ({
        id: String(stage),
        value: String(stage),
        label: jobRequestStageDict[stage]
    }))

export const jobRequestPipelineStageOptions: optionType[] = [
    JobRequestStage.NEW,
    JobRequestStage.INTERVIEW,
    JobRequestStage.OFFER,
    JobRequestStage.HIRED
].map((stage) => ({
    id: String(stage),
    value: String(stage),
    label: jobRequestStageDict[stage]
}))

export const jobRequestKanbanStageOptions: optionType[] = [
    {
        id: String(JobRequestStage.DECLINED),
        value: String(JobRequestStage.DECLINED),
        label: jobRequestStageDict[JobRequestStage.DECLINED]
    },
    ...jobRequestPipelineStageOptions
]

export function getJobRequestStage(request: Pick<JobRequestFormData, 'stage' | 'status'>): JobRequestStage {
    const storedStage = Number(request.stage)

    if (storedStage === JobRequestStage.SHORTLISTED || storedStage === 3) {
        return JobRequestStage.INTERVIEW
    }

    if (storedStage === JobRequestStage.SOFT_NO) {
        return JobRequestStage.NEW
    }

    if (typeof request.stage === 'number' && Object.values(JobRequestStage).includes(request.stage)) {
        return request.stage
    }

    switch (request.status) {
        case JobRequestStatus.ACCEPTED:
            return JobRequestStage.HIRED
        case JobRequestStatus.REJECTED:
            return JobRequestStage.DECLINED
        default:
            return JobRequestStage.NEW
    }
}

export function getJobRequestStatusForStage(stage: JobRequestStage): JobRequestStatus {
    if (stage === JobRequestStage.HIRED) {
        return JobRequestStatus.ACCEPTED
    }

    if (stage === JobRequestStage.DECLINED) {
        return JobRequestStatus.REJECTED
    }

    return JobRequestStatus.APPLIED
}

export function getApplicantVisibleJobRequest(request: JobRequest): JobRequest {
    const visibleStage =
        request.status === JobRequestStatus.ACCEPTED
            ? JobRequestStage.HIRED
            : request.status === JobRequestStatus.REJECTED
              ? JobRequestStage.DECLINED
              : JobRequestStage.NEW

    return {
        ...request,
        internalRejectReason: undefined,
        notes: undefined,
        score: null,
        scoreUpdatedAt: undefined,
        scoreUpdatedById: undefined,
        contractType: null,
        salary: null,
        stage: visibleStage,
        stageUpdatedAt: undefined,
        stageUpdatedById: undefined
    }
}

export type Applicant = UserName & {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    cvUrl?: string
    clUrl?: string
    consents?: ConsentsData
}

export interface ApplicantData {
    applicant?: {
        firstName: string
        lastName: string
        consents?: ConsentsData
    }
    userId?: UserId
    applicantId?: UserId
}

export type JobApplicationFormData = {
    firstName?: string
    lastName?: string
    cvFile?: File
    email: string
    phone: string
    motivation: string
    jobMediationAgreementAccepted?: boolean
    jobApplicationConsentAccepted: boolean
    consents?: ConsentsData
}

export type UnregisteredUser = Applicant & {
    firstName?: string
    lastName?: string
}

export interface JobOffer {
    startDate: Timestamp
    salary: number
    salaryUnit: string
}

// A single self-assessment (sebapoznanie) result the candidate opted in to share with the employer.
// This is a denormalized snapshot stored on the job request itself — recruiters can read the job
// request but NOT the candidate's private surveys, so sharing happens only via explicit consent here.
export interface SharedAssessment {
    type: SurveyTypes
    title: string
    icon?: string
    // Display-friendly summary (mirrors SurveyResultDisplay), carrying the actual values.
    primaryLabel: string
    secondaryLabel?: string
    details?: string[]
    // What the result means at work, written for the company (see employerSummaries.ts)
    summary?: string
    // Keys of what stands out in the result (Holland letters, Big Five traits, …), the same ones the
    // tags show. Lets the fit with a position (jobFit.ts) work from exactly what was shared.
    traits?: string[]
}

// Internal recruiter note on a candidate (e.g. recap of a phone call). Org-only, never shown to applicant.
export interface JobRequestNote {
    id: string
    text: string
    authorId?: UserId
    authorName?: string
    createdAt: Timestamp
}

export type JobRequestFormData = {
    cvUrl?: string
    clUrl?: string | string[]
    email: string
    phone: string
    motivation: string
    about: string
    fit?: string // temporarily removed
    growth?: string // temporarily removed
    question?: string // temporarily removed
    linkedInUrl: string
    acceptTerms: boolean
    status: JobRequestStatus
    stage?: JobRequestStage
    score?: number | null
    // Proposed contract type for the candidate (HR orientation, not final contract)
    contractType?: JobRequestContractType | null
    // Proposed monthly compensation in EUR (HR orientation, not final contract)
    salary?: number | null
    offer?: JobOffer
    applicant?: UnregisteredUser
    messageToApplicant?: string
    internalRejectReason?: string
    consents?: ConsentsData
    // Self-assessment results the candidate explicitly opted in to share with this employer.
    // Snapshot taken at apply time and refreshed while the application is still open.
    sharedAssessmentsConsent?: boolean
    sharedAssessments?: SharedAssessment[]
    sharedAssessmentsUpdatedAt?: Timestamp
    // The candidate agreed to add career tests they complete while the application is open
    sharedAssessmentsIncludeLater?: boolean
    // Tests the candidate already had a result from when deciding what to share. Only tests
    // outside this list are added later, so a result they left unchecked is never shared.
    sharedAssessmentsOfferedTypes?: SurveyTypes[]
}

export type JobRequestId = string

export type JobRequest = DocumentWithTimestamps &
    JobRequestFormData & {
        id: JobRequestId
        applicantId?: UserId
        jobId: string
        orgId: string
        platformNotified?: boolean
        scoreUpdatedAt?: Timestamp
        scoreUpdatedById?: UserId
        stageUpdatedAt?: Timestamp
        stageUpdatedById?: UserId
        // Outcome as reported by the applicant themselves. Deliberately independent of the
        // org-side `status` — an applicant hired after the org closed the request on-platform
        // is the discrepancy we want to be able to see. Correctable, so it may change over time.
        statusByUser?: Exclude<JobRequestStatus, JobRequestStatus.APPLIED>
        statusByUserUpdatedAt?: Timestamp
        // Free-text note the applicant may leave alongside their answer.
        statusByUserComment?: string
        // Preserved across later corrections, so "when did they first tell us" stays measurable.
        statusByUserFirstAnsweredAt?: Timestamp
        // Set when an admin emailed the org about this outcome, so the billing worklist can
        // show what has already been chased.
        outcomeEmailSentAt?: Timestamp
        outcomeEmailSentById?: UserId
        outcomeEmailSubject?: string
        // Admin-driven follow-up pipeline; absent means JobOutcomeStage.NEW.
        outcomeStage?: JobOutcomeStage
        outcomeStageUpdatedAt?: Timestamp
        outcomeStageUpdatedById?: UserId
        /** Free-text working note for the case (who we spoke to, what was agreed). */
        outcomeNote?: string
        acceptedAt?: Timestamp
        rejectedAt?: Timestamp
        rejectedById?: UserId
        rejectEmailSentAt?: Timestamp
        // Candidate-facing offer (non-binding proposal sent by email) tracking
        offerSentAt?: Timestamp
        offerSentById?: UserId
        offerMessage?: string
        // Internal recruiter notes (chronological), org-only — stripped before exposing to applicant
        notes?: JobRequestNote[]
    }

export type JobCandidate = {
    label?: string
    salary?: number
    salaryUnit?: string
    startDate: Date | Timestamp
    id?: UserId
    requestId: JobRequestId
    status: JobRequestStatus
}

export const DefaultJobRequest: JobRequestFormData = {
    email: '',
    phone: '',
    motivation: '',
    about: '',
    linkedInUrl: '',
    acceptTerms: false,
    status: JobRequestStatus.APPLIED,
    stage: JobRequestStage.NEW,
    score: null
}

export const findEmployeeOptions: optionType[] = [
    { id: 'success', value: 'success', label: 'ÁNO' },
    { id: 'noSuccess', value: 'noSuccess', label: 'NIE' },
    {
        id: 'notDecided',
        value: 'notDecided',
        label: 'ešte sme výberový proces neuzavreli'
    }
]

export enum NotFoundEmpReason {
    extern = 'extern',
    anotherPortal = 'another-portal',
    linkedin = 'linkedin',
    none = 'none',
    other = 'other'
}

export const notFoundEmpReasonDict: { [key in NotFoundEmpReason]: string } = {
    [NotFoundEmpReason.extern]: 'Vybrali sme iného uchádzača: interne / vďaka headhunterovi',
    [NotFoundEmpReason.anotherPortal]: 'Vybrali sme iného uchádzača cez iný pracovný portál',
    [NotFoundEmpReason.linkedin]: 'Vybrali sme iného uchádzača cez Linkedin',
    [NotFoundEmpReason.none]: 'Nevybrali sme žiadneho uchádzača',
    [NotFoundEmpReason.other]: 'Iný dôvod:'
}

export const notFoundEmployeeOptions: optionType[] = Object.values(NotFoundEmpReason).map((id) => ({
    id,
    value: id,
    label: notFoundEmpReasonDict[id]
}))

// Search search type for job requests
export type SearchJobRequest = Omit<JobRequest, 'consents' | 'message'> & {
    objectID?: string
}
